import { Injectable } from '@angular/core';
import { AdsAttributionPayload } from 'app/core/models/ads-attribution.model';
import { environment } from 'environments/environment';

interface AppInsightsExceptionTelemetry {
    exception: Error;
}

interface AppInsightsEventTelemetry {
    name: string;
}

interface AppInsightsTraceTelemetry {
    message: string;
}

interface AppInsightsClient {
    loadAppInsights(): void;
    trackException(telemetry: AppInsightsExceptionTelemetry, customProperties?: Record<string, string>): void;
    trackEvent(telemetry: AppInsightsEventTelemetry, customProperties?: Record<string, string>): void;
    trackTrace(telemetry: AppInsightsTraceTelemetry, customProperties?: Record<string, string>): void;
}

interface AppInsightsConstructor {
    new (options: { config: Record<string, unknown> }): AppInsightsClient;
}

interface AuthFailureRecord {
    error: string;
    errorDescription: string;
    state: string;
    route: string;
    referenceNumber: string;
    signature: string;
    recordedAt: string;
}

export interface LatestAuthFailure {
    error: string;
    errorDescription: string;
    state: string;
    route: string;
    referenceNumber: string;
    recordedAt: string;
}

interface GoogleAdsAttributionRecord {
    gclid: string;
    gbraid: string;
    wbraid: string;
    source: 'cookie';
    landingUrl: string;
    referrer: string;
    recordedAt: string;
}

interface MetaAdsAttributionRecord {
    fbclid: string;
    fbc: string;
    fbp: string;
    source: 'cookie';
    landingUrl: string;
    referrer: string;
    recordedAt: string;
}

interface UtmAttributionRecord {
    term: string;
    content: string;
    campaign: string;
    medium: string;
    source: string;
    recordedAtUtc: string;
}

declare global {
    interface Window {
        Microsoft?: {
            ApplicationInsights?: {
                ApplicationInsights?: AppInsightsConstructor;
            };
        };
    }
}

@Injectable({ providedIn: 'root' })
export class TelemetryService {
    private static readonly authFailureStorageKey = 'openflis.auth.callback.failure';
    private static readonly googleAdsAttributionStorageKey = 'openflis.google.ads.attribution';
    private static readonly metaAdsAttributionStorageKey = 'openflis.meta.ads.attribution';
    private static readonly googleAdsCookieNames = {
        gclid: 'openflis_gclid',
        gbraid: 'openflis_gbraid',
        wbraid: 'openflis_wbraid'
    };
    private static readonly metaAdsCookieNames = {
        fbclid: 'openflis_fbclid',
        fbc: '_fbc',
        fbp: '_fbp'
    };
    private static readonly utmCookieNames = {
        originalCampaign: 'openflis_original_utm_campaign',
        originalMedium: 'openflis_original_utm_medium',
        originalSource: 'openflis_original_utm_source',
        originalRecordedAtUtc: 'openflis_original_utm_recorded_at_utc',
        mostRecentCampaign: 'openflis_most_recent_utm_campaign',
        mostRecentMedium: 'openflis_most_recent_utm_medium',
        mostRecentSource: 'openflis_most_recent_utm_source',
        mostRecentRecordedAtUtc: 'openflis_most_recent_utm_recorded_at_utc'
    };
    private client?: AppInsightsClient;
    private isLoading = false;
    private pending: Array<() => void> = [];

    constructor() {
        this.initialize();
    }

    public captureAuthCallbackFailureFromUrl(url: string = typeof window === 'undefined' ? '' : window.location.href): string | null {
        if (!url) {
            return null;
        }

        const parsedUrl = this.parseUrl(url);
        if (!parsedUrl || !parsedUrl.pathname.endsWith('/callback')) {
            return null;
        }

        const error = parsedUrl.searchParams.get('error') ?? undefined;
        const errorDescription = parsedUrl.searchParams.get('error_description') ?? undefined;
        const state = parsedUrl.searchParams.get('state') ?? undefined;

        if (!error && !errorDescription) {
            return null;
        }

        return this.trackAuthCallbackFailure(error, errorDescription, state, parsedUrl.pathname);
    }

    public trackAuthCallbackFailure(
        error: string | undefined,
        errorDescription: string | undefined,
        state: string | undefined,
        route: string = typeof window === 'undefined' ? '' : window.location.pathname
    ): string {
        const signature = [error ?? '', errorDescription ?? '', state ?? '', route].join('|');
        const existing = this.getStoredAuthFailure();
        if (existing?.signature === signature) {
            return existing.referenceNumber;
        }

        const referenceNumber = this.generateReferenceNumber();
        const record: AuthFailureRecord = {
            error: error ?? '',
            errorDescription: errorDescription ?? '',
            state: state ?? '',
            route,
            referenceNumber,
            signature,
            recordedAt: new Date().toISOString()
        };

        this.storeAuthFailure(record);
        const properties: Record<string, string> = {
            area: 'auth.callback',
            authError: record.error,
            authErrorDescription: record.errorDescription,
            authState: record.state,
            route: record.route,
            referenceNumber: record.referenceNumber,
            recordedAt: record.recordedAt
        };

        const exception = new Error(errorDescription || error || 'Authentication callback failed.');
        exception.name = 'AuthCallbackFailure';

        this.enqueue(() => {
            this.client?.trackException({ exception }, properties);
            this.client?.trackEvent({ name: 'auth_callback_failure' }, properties);
            this.client?.trackTrace({ message: 'Auth callback failure.' }, properties);
        });

        return referenceNumber;
    }

    public getLatestAuthFailureReference(): string | null {
        return this.getStoredAuthFailure()?.referenceNumber ?? null;
    }

    public getLatestAuthFailure(): LatestAuthFailure | null {
        const failure = this.getStoredAuthFailure();
        if (!failure) {
            return null;
        }

        return {
            error: failure.error,
            errorDescription: failure.errorDescription,
            state: failure.state,
            route: failure.route,
            referenceNumber: failure.referenceNumber,
            recordedAt: failure.recordedAt
        };
    }

    public captureGoogleAdsAttribution(url: string = typeof window === 'undefined' ? '' : window.location.href) {
        const cookieAttribution = this.getGoogleAdsAttributionFromCookies(url);
        if (cookieAttribution) {
            this.storeGoogleAdsAttribution(cookieAttribution);
        }
    }

    public captureMetaAdsAttribution(url: string = typeof window === 'undefined' ? '' : window.location.href) {
        this.ensureMetaClickCookies();
        const cookieAttribution = this.getMetaAdsAttributionFromCookies(url);
        if (cookieAttribution) {
            this.storeMetaAdsAttribution(cookieAttribution);
        }
    }

    public hasGoogleAdsAttribution(): boolean {
        return this.getStoredGoogleAdsAttribution() !== null;
    }

    public hasMetaAdsAttribution(): boolean {
        return this.getStoredMetaAdsAttribution() !== null;
    }

    public getAdsAttributionPayload(): AdsAttributionPayload | null {
        this.captureGoogleAdsAttribution();
        this.captureMetaAdsAttribution();

        const googleAds = this.getStoredGoogleAdsAttribution();
        const metaAds = this.getStoredMetaAdsAttribution();
        const originalUtm = this.getOriginalUtmAttributionFromCookies();
        const mostRecentUtm = this.getMostRecentUtmAttributionFromCookies();
        const cookies = this.readCookies();
        const landingUrl = cookies['openflis_original_landing_page'] ?? '';
        const referrer = cookies['openflis_original_referrer'] ?? '';

        if (!googleAds && !metaAds && !originalUtm && !mostRecentUtm && !landingUrl && !referrer) {
            return null;
        }

        return {
            landingUrl: landingUrl || undefined,
            referrer: landingUrl || referrer ? referrer : undefined,
            googleAds: googleAds
                ? {
                    gclid: googleAds.gclid,
                    gbraid: googleAds.gbraid,
                    wbraid: googleAds.wbraid,
                    landingUrl: googleAds.landingUrl,
                    referrer: googleAds.referrer
                }
                : undefined,
            metaAds: metaAds
                ? {
                    fbclid: metaAds.fbclid,
                    fbc: metaAds.fbc,
                    fbp: metaAds.fbp,
                    landingUrl: metaAds.landingUrl,
                    referrer: metaAds.referrer
                }
                : undefined,
            originalUtm: originalUtm
                ? {
                    campaign: originalUtm.campaign,
                    term: originalUtm.term,
                    content: originalUtm.content,
                    medium: originalUtm.medium,
                    source: originalUtm.source,
                    recordedAtUtc: originalUtm.recordedAtUtc
                }
                : undefined,
            mostRecentUtm: mostRecentUtm
                ? {
                    campaign: mostRecentUtm.campaign,
                    term: mostRecentUtm.term,
                    content: mostRecentUtm.content,
                    medium: mostRecentUtm.medium,
                    source: mostRecentUtm.source,
                    recordedAtUtc: mostRecentUtm.recordedAtUtc
                }
                : undefined
        };
    }

    public trackGoogleAdsConversionAttempt(args: {
        conversionType: 'signup' | 'upgrade',
        sendTo: string,
        userId?: string,
        fired: boolean,
        gtagAvailable: boolean
    }) {
        const attribution = this.getStoredGoogleAdsAttribution();
        const properties: Record<string, string> = {
            area: 'google.ads.conversion',
            conversionType: args.conversionType,
            sendTo: args.sendTo,
            userId: args.userId ?? '',
            fired: String(args.fired),
            gtagAvailable: String(args.gtagAvailable),
            route: typeof window === 'undefined' ? '' : window.location.pathname,
            gclid: attribution?.gclid ?? '',
            gbraid: attribution?.gbraid ?? '',
            wbraid: attribution?.wbraid ?? '',
            attributionSource: attribution?.source ?? '',
            landingUrl: attribution?.landingUrl ?? '',
            referrer: attribution?.referrer ?? '',
            attributionRecordedAt: attribution?.recordedAt ?? ''
        };

        this.enqueue(() => {
            this.client?.trackEvent({ name: 'google_ads_conversion_attempt' }, properties);
            this.client?.trackTrace({ message: 'Google Ads conversion attempt.' }, properties);
        });
    }

    public trackMetaAdsConversionAttempt(args: {
        conversionType: 'signup' | 'upgrade',
        pixelId: string,
        eventName: string,
        userId?: string,
        fired: boolean,
        fbqAvailable: boolean
    }) {
        const attribution = this.getStoredMetaAdsAttribution();
        const properties: Record<string, string> = {
            area: 'meta.ads.conversion',
            conversionType: args.conversionType,
            pixelId: args.pixelId,
            eventName: args.eventName,
            userId: args.userId ?? '',
            fired: String(args.fired),
            fbqAvailable: String(args.fbqAvailable),
            route: typeof window === 'undefined' ? '' : window.location.pathname,
            fbclid: attribution?.fbclid ?? '',
            fbc: attribution?.fbc ?? '',
            fbp: attribution?.fbp ?? '',
            attributionSource: attribution?.source ?? '',
            landingUrl: attribution?.landingUrl ?? '',
            referrer: attribution?.referrer ?? '',
            attributionRecordedAt: attribution?.recordedAt ?? ''
        };

        this.enqueue(() => {
            this.client?.trackEvent({ name: 'meta_ads_conversion_attempt' }, properties);
            this.client?.trackTrace({ message: 'Meta Ads conversion attempt.' }, properties);
        });
    }

    public trackUiConversionApiRequest(args: {
        conversionType: 'signup' | 'upgrade',
        requestMethod: string,
        requestUrl: string,
        requestBody: unknown,
        userId?: string
    }) {
        const properties: Record<string, string> = {
            area: 'ui.conversion.api.request',
            conversionType: args.conversionType,
            requestMethod: args.requestMethod,
            requestUrl: args.requestUrl,
            requestBody: this.serializeTelemetryValue(args.requestBody),
            userId: args.userId ?? '',
            route: typeof window === 'undefined' ? '' : window.location.pathname
        };

        this.enqueue(() => {
            this.client?.trackEvent({ name: 'ui_conversion_api_request' }, properties);
            this.client?.trackTrace({ message: 'UI conversion API request.' }, properties);
        });
    }

    public trackUiSignupWithoutAdAttribution(args: {
        requestMethod: string,
        requestUrl: string,
        requestBody: unknown,
        userId?: string
    }) {
        const properties: Record<string, string> = {
            area: 'ui.signup.attribution',
            conversionType: 'signup',
            hasAdAttribution: 'false',
            requestMethod: args.requestMethod,
            requestUrl: args.requestUrl,
            requestBody: this.serializeTelemetryValue(args.requestBody),
            userId: args.userId ?? '',
            route: typeof window === 'undefined' ? '' : window.location.pathname
        };

        this.enqueue(() => {
            this.client?.trackEvent({ name: 'ui_signup_without_ad_attribution' }, properties);
            this.client?.trackTrace({ message: 'UI signup request had no ad attribution.' }, properties);
        });
    }

    private initialize() {
        const connectionString = environment.appInsightsConnectionString;
        if (!connectionString || typeof document === 'undefined') {
            return;
        }

        const existingClient = window.Microsoft?.ApplicationInsights?.ApplicationInsights;
        if (this.client) {
            return;
        }

        if (existingClient) {
            this.createClient(existingClient, connectionString);
            return;
        }

        if (this.isLoading) {
            return;
        }

        this.isLoading = true;

        const script = document.createElement('script');
        script.src = 'https://js.monitor.azure.com/scripts/b/ai.3.gbl.min.js';
        script.async = true;
        script.onload = () => {
            this.isLoading = false;

            const constructor = window.Microsoft?.ApplicationInsights?.ApplicationInsights;
            if (!constructor) {
                return;
            }

            this.createClient(constructor, connectionString);
        };
        script.onerror = () => {
            this.isLoading = false;
            this.pending = [];
        };

        document.head.appendChild(script);
    }

    private createClient(constructor: AppInsightsConstructor, connectionString: string) {
        this.client = new constructor({
            config: {
                connectionString,
                enableAutoRouteTracking: true
            }
        });
        this.client.loadAppInsights();
        this.flushPending();
    }

    private enqueue(action: () => void) {
        if (this.client) {
            action();
            return;
        }

        this.pending.push(action);
        this.initialize();
    }

    private flushPending() {
        const pending = [...this.pending];
        this.pending = [];
        pending.forEach(action => action());
    }

    private serializeTelemetryValue(value: unknown): string {
        if (typeof value === 'string') {
            return value;
        }

        if (value === undefined) {
            return '';
        }

        try {
            return JSON.stringify(value);
        } catch {
            return '[unserializable]';
        }
    }

    private parseUrl(url: string): URL | null {
        try {
            return new URL(url, typeof window === 'undefined' ? 'https://app.openflis.com' : window.location.origin);
        } catch {
            return null;
        }
    }

    private generateReferenceNumber(): string {
        const timestamp = Date.now().toString(36).toUpperCase();
        const random = Math.random().toString(36).slice(2, 8).toUpperCase();
        return `AUTH-${timestamp}-${random}`;
    }

    private getStoredAuthFailure(): AuthFailureRecord | null {
        if (typeof sessionStorage === 'undefined') {
            return null;
        }

        const value = sessionStorage.getItem(TelemetryService.authFailureStorageKey);
        if (!value) {
            return null;
        }

        try {
            return JSON.parse(value) as AuthFailureRecord;
        } catch {
            return null;
        }
    }

    private storeAuthFailure(record: AuthFailureRecord) {
        if (typeof sessionStorage === 'undefined') {
            return;
        }

        sessionStorage.setItem(TelemetryService.authFailureStorageKey, JSON.stringify(record));
    }

    private ensureMetaClickCookies() {
        const cookies = this.readCookies();
        const sharedFbclid = cookies[TelemetryService.metaAdsCookieNames.fbclid] ?? '';
        const existingFbc = cookies[TelemetryService.metaAdsCookieNames.fbc] ?? '';

        if (!sharedFbclid || existingFbc) {
            return;
        }

        this.writeCookie(
            TelemetryService.metaAdsCookieNames.fbc,
            this.buildMetaClickCookie(sharedFbclid),
            60 * 60 * 24 * 90
        );
    }

    private getGoogleAdsAttributionFromCookies(landingUrl: string): GoogleAdsAttributionRecord | null {
        const cookies = this.readCookies();
        const gclid = cookies[TelemetryService.googleAdsCookieNames.gclid] ?? '';
        const gbraid = cookies[TelemetryService.googleAdsCookieNames.gbraid] ?? '';
        const wbraid = cookies[TelemetryService.googleAdsCookieNames.wbraid] ?? '';

        if (!gclid && !gbraid && !wbraid) {
            return null;
        }

        return {
            gclid,
            gbraid,
            wbraid,
            source: 'cookie',
            landingUrl,
            referrer: typeof document === 'undefined' ? '' : document.referrer,
            recordedAt: new Date().toISOString()
        };
    }

    private getMetaAdsAttributionFromCookies(landingUrl: string): MetaAdsAttributionRecord | null {
        const cookies = this.readCookies();
        const fbclid = cookies[TelemetryService.metaAdsCookieNames.fbclid] ?? '';
        const fbc = cookies[TelemetryService.metaAdsCookieNames.fbc] ?? '';
        const fbp = cookies[TelemetryService.metaAdsCookieNames.fbp] ?? '';

        if (!fbclid && !fbc) {
            return null;
        }

        return {
            fbclid,
            fbc,
            fbp,
            source: 'cookie',
            landingUrl,
            referrer: typeof document === 'undefined' ? '' : document.referrer,
            recordedAt: new Date().toISOString()
        };
    }

    private getOriginalUtmAttributionFromCookies(): UtmAttributionRecord | null {
        return this.getUtmAttributionFromCookies({
            term: 'openflis_original_utm_term',
            content: 'openflis_original_utm_content',
            campaign: TelemetryService.utmCookieNames.originalCampaign,
            medium: TelemetryService.utmCookieNames.originalMedium,
            source: TelemetryService.utmCookieNames.originalSource,
            recordedAtUtc: TelemetryService.utmCookieNames.originalRecordedAtUtc
        });
    }

    private getMostRecentUtmAttributionFromCookies(): UtmAttributionRecord | null {
        return this.getUtmAttributionFromCookies({
            term: 'openflis_most_recent_utm_term',
            content: 'openflis_most_recent_utm_content',
            campaign: TelemetryService.utmCookieNames.mostRecentCampaign,
            medium: TelemetryService.utmCookieNames.mostRecentMedium,
            source: TelemetryService.utmCookieNames.mostRecentSource,
            recordedAtUtc: TelemetryService.utmCookieNames.mostRecentRecordedAtUtc
        });
    }

    private getUtmAttributionFromCookies(cookieNames: {
        term: string,
        content: string,
        campaign: string,
        medium: string,
        source: string,
        recordedAtUtc: string
    }): UtmAttributionRecord | null {
        const cookies = this.readCookies();
        const campaign = cookies[cookieNames.campaign] ?? '';
        const term = cookies[cookieNames.term] ?? '';
        const content = cookies[cookieNames.content] ?? '';
        const medium = cookies[cookieNames.medium] ?? '';
        const source = cookies[cookieNames.source] ?? '';
        const recordedAtUtc = cookies[cookieNames.recordedAtUtc] ?? '';

        if (!campaign && !medium && !source && !term && !content) {
            return null;
        }

        return {
            campaign,
            term,
            content,
            medium,
            source,
            recordedAtUtc
        };
    }

    private getStoredGoogleAdsAttribution(): GoogleAdsAttributionRecord | null {
        if (typeof sessionStorage === 'undefined') {
            return null;
        }

        const value = sessionStorage.getItem(TelemetryService.googleAdsAttributionStorageKey);
        if (!value) {
            return null;
        }

        try {
            return JSON.parse(value) as GoogleAdsAttributionRecord;
        } catch {
            return null;
        }
    }

    private storeGoogleAdsAttribution(record: GoogleAdsAttributionRecord) {
        if (typeof sessionStorage === 'undefined') {
            return;
        }

        sessionStorage.setItem(TelemetryService.googleAdsAttributionStorageKey, JSON.stringify(record));
    }

    private getStoredMetaAdsAttribution(): MetaAdsAttributionRecord | null {
        if (typeof sessionStorage === 'undefined') {
            return null;
        }

        const value = sessionStorage.getItem(TelemetryService.metaAdsAttributionStorageKey);
        if (!value) {
            return null;
        }

        try {
            return JSON.parse(value) as MetaAdsAttributionRecord;
        } catch {
            return null;
        }
    }

    private storeMetaAdsAttribution(record: MetaAdsAttributionRecord) {
        if (typeof sessionStorage === 'undefined') {
            return;
        }

        sessionStorage.setItem(TelemetryService.metaAdsAttributionStorageKey, JSON.stringify(record));
    }

    private buildMetaClickCookie(fbclid: string): string {
        return `fb.1.${Date.now()}.${fbclid}`;
    }

    private writeCookie(name: string, value: string, maxAgeSeconds: number) {
        if (typeof document === 'undefined' || !value) {
            return;
        }

        const secure = typeof window !== 'undefined' && window.location.protocol === 'https:'
            ? '; Secure'
            : '';
        const domain = this.getSharedCookieDomain();
        const domainAttribute = domain ? `; Domain=${domain}` : '';

        document.cookie =
            `${name}=${encodeURIComponent(value)}` +
            domainAttribute +
            '; Path=/' +
            `; Max-Age=${maxAgeSeconds}` +
            '; SameSite=Lax' +
            secure;
    }

    private getSharedCookieDomain(): string | null {
        if (typeof window === 'undefined') {
            return null;
        }

        const hostname = window.location.hostname.toLowerCase();
        return hostname === 'openflis.com' || hostname.endsWith('.openflis.com')
            ? '.openflis.com'
            : null;
    }

    private readCookies(): Record<string, string> {
        if (typeof document === 'undefined' || !document.cookie) {
            return {};
        }

        return document.cookie
            .split(';')
            .map(value => value.trim())
            .filter(Boolean)
            .reduce<Record<string, string>>((cookies, entry) => {
                const separatorIndex = entry.indexOf('=');
                const name = separatorIndex >= 0 ? entry.slice(0, separatorIndex) : entry;
                const value = separatorIndex >= 0 ? entry.slice(separatorIndex + 1) : '';
                cookies[name] = decodeURIComponent(value);
                return cookies;
            }, {});
    }
}
